import { useEffect, useMemo, useRef, useState } from "react"
import { Check, Loader2 } from "lucide-react"

import BrandLockup from "@/components/tuw/auth/BrandLockup"
import { WizardShell } from "@/components/tuw/post/WizardShell"
import { RAIL_BAR_BAND } from "@/components/tuw/post/PriceRail"
import { STEPPER_COLLAPSE } from "@/components/tuw/post/Stepper"
import { StepFields } from "@/components/tuw/openHouse/StepFields"
import { ReviewStep } from "@/components/tuw/openHouse/ReviewStep"
import { GuestPriceRail, PriceNotice, ReviewPrice } from "@/components/tuw/openHouse/GuestPrice"
import { Bookmark, MessagePage, SaveLaterButton, linkPage, openReturnLink } from "@/components/tuw/openHouse/Bookmark"
import { AccountScreen } from "@/components/tuw/openHouse/AccountScreen"
import { Arrival, StartPage } from "@/components/tuw/openHouse/Arrival"
import WaitingScreen, { PostExpired, WaitingColumn } from "@/components/tuw/openHouse/WaitingScreen"
import { InvitationStrip, arrivedInviting, removeInvitation } from "@/components/tuw/viewingRoom/Invitation"
import { useDraftSave } from "@/lib/post/useDraftSave"
import { track } from "@/lib/openHouse/analytics"
import { BUNDLE_COMPOSITION, PLATINUM } from "@/lib/post/bundle"
import { OUTLINE_MAX_BYTES, isOutlineFile, issueTarget, reviewSections, stepIssues } from "@/lib/openHouse/bookWritingSteps"
import { answerUpdate, initialState, openHousePayload, withoutUploaded } from "@/lib/openHouse/draft"
import {
  ACCOUNT_PATH, GUEST_DRAFT, acceptGuestPrice, catalogLevelsRange, changeLines, continueToAccount, emailSaveLink, fetchQuote, guestAnswers,
  guestState, openPostedProject, postingPayload, quoteAmounts, quoteDisplay, quoteScope, quotedOutline, rememberWaiting,
  reserveAccount, resumeGuestDraft, saveGuestDraft, wasWaiting,
} from "@/lib/openHouse/guest"
import { postingCtx, serviceOf } from "@/lib/openHouse/services"
import { CONFIRMATION, GUEST } from "@/lib/openHouse/wording"

// Open House front porch (spec Front porch, P3-H4; BRIEF-2 steps 1–3): a
// visitor with no account builds a book writing, sample or bundle project
// through the signed-in Open House steps (the same step definitions and
// components) and sees the server's exact price. Answers save to the guest
// draft (POST, then PATCH by revision) and a refresh resumes it (GET), with any
// price change to accept. Posting hands off to Reservation (continueToAccount).
// Any step can save the draft by email (Bookmark), whose link opens it here
// as a resume does, or shows why it can't.
//
// Reservation (6e-2a; BRIEF-2 step 5): served at /post-project/account, the page
// opens the guest's draft on its account screen (AccountScreen), which posts it
// by creating an account that waits for confirmation. A reserved draft shows
// Key turn's waiting screen there (WaitingScreen); a refusal about the draft
// itself returns to the steps.
//
// One nudge (P3-H9): the reminder email's button opens /post-project/account
// marked #reminder. Where no draft opens (another device or browser, or a
// post deleted at its deadline) it says so (§6 "Opened on another device"),
// never the draft-expired lines.

const onAccountPath = () => window.location.pathname === ACCOUNT_PATH
// Read once, then cleared from the address.
function fromReminder() {
  if (window.location.hash !== "#reminder") return false
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)
  return true
}
// Back on the steps, the address is the steps' own again.
const leaveAccount = () => onAccountPath() && window.history.replaceState(null, "", "/post-project")

function focusField(key) {
  const node = document.querySelector(`[data-post-field="${key}"]`)
  node?.scrollIntoView({ behavior: "smooth", block: "center" })
  node?.querySelector("input, textarea, button, [tabindex]:not([tabindex='-1'])")?.focus({ preventScroll: true })
}

// A quote's price for analytics: the total, or the lowest for open writer levels.
const priceParams = (quote) => (quote ? { value: Number(quote.openLevels ? quote.minimum.total : quote.total), currency: quote.currency } : {})

function Alert({ tone = "bad", children, action, onAction, busy }) {
  const tones = { bad: "border-destructive/30 bg-destructive/10 text-destructive", info: "border-border bg-accent text-foreground" }
  return (
    <div role={tone === "bad" ? "alert" : "status"} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-[13px] ${tones[tone]}`}>
      <span className="min-w-0 flex-1">{children}</span>
      {action && (
        <button type="button" onClick={onAction} disabled={busy} className="font-semibold underline underline-offset-2 disabled:opacity-50">
          {action}
        </button>
      )}
    </div>
  )
}

// Book writing arrives open from the site's book and bundle links (?start=writing).
const arrivesWriting = () => new URLSearchParams(window.location.search).get("start") === "writing"

export default function OpenHouseGuest({ catalog = {}, links = {}, account = {}, draftInactivityDays, startPage = false }) {
  const [phase, setPhase] = useState("loading") // loading | entry | wizard | account | waiting | expired | link | reminderNoDraft
  const [waiting, setWaiting] = useState(null) // the reservation's waiting screen state
  const [linkLines, setLinkLines] = useState(null)
  const [bookmarkOpen, setBookmarkOpen] = useState(false)
  const [bookmark, setBookmark] = useState({}) // the last save link's { email, reminderConsent }
  const [state, setState] = useState(() => initialState({}, null, "book_writing"))
  const [showErr, setShowErr] = useState(false)
  const [terms, setTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [upload, setUpload] = useState({ status: "idle", file: null })
  const [notice, setNotice] = useState(null) // restored | stale
  const [invitation, setInvitation] = useState(null) // the freelancer the guest invited (Viewing Room)
  const [dirty, setDirty] = useState(false)
  const [quote, setQuote] = useState(null)
  const [quoteEpoch, setQuoteEpoch] = useState(0)
  const [change, setChange] = useState(null)
  // The answers' scope signature a just-accepted price was for.
  const [accepted, setAccepted] = useState(null)
  // Counts answer edits, so an acceptance whose answers changed and changed
  // back while it was in flight (same signature) is still dropped.
  const editsRef = useRef(0)
  const [accepting, setAccepting] = useState(false)
  const stateRef = useRef(state)
  stateRef.current = state
  const revisionRef = useRef(null)
  const uploadedRef = useRef(new WeakSet())
  const removedRef = useRef(new Set())
  const removalRef = useRef(0)
  const quoteRun = useRef({ busy: false, again: false })
  // Bumped whenever a quote is adopted from elsewhere, so an older request's answer is dropped.
  const quoteGen = useRef(0)
  const quoteRef = useRef(quote)
  quoteRef.current = quote
  const topRef = useRef(null)
  const fieldNodes = useRef(new Map())

  const ctx = useMemo(
    () => postingCtx(catalog, state.service, state.posting.bundle_tier),
    [catalog, state.service, state.posting.bundle_tier]
  )
  const service = serviceOf(state.service)
  const values = useMemo(() => ({ ...state.posting, short_description: state.oh.short_description }), [state.posting, state.oh])
  const steps = service.steps(state.posting, ctx)
  const index = Math.min(state.stepIndex, steps.length - 1)
  const step = steps[index]
  const isReview = step.id === "review"

  // Every draft write rides one queue, accepting a price and removing the
  // invitation included: the answers save first when edited, then that write
  // sends the revision the save returned. A newer price is handed back rather
  // than failing the save.
  const { savedAt, saveError, retryingSave, noteDraftEdit, saveCurrentDraft, retrySave } = useDraftSave({
    saveDraft: async (_id, payload) => {
      let draft
      if (!(payload.accept || payload.uninvite) || payload.saveFirst) {
        draft = await saveGuestDraft(revisionRef.current, payload)
        revisionRef.current = draft.revision
      }
      if (payload.uninvite) {
        draft = (await removeInvitation(revisionRef.current, payload.uninvite)).draft
        revisionRef.current = draft.revision
        return draft
      }
      if (!payload.accept) return draft
      try {
        draft = (await acceptGuestPrice(revisionRef.current, payload.accept)).draft
      } catch (err) {
        if (err?.reason === "price_changed" && err.data?.quote) return { priceChanged: err.data.quote }
        throw err
      }
      revisionRef.current = draft.revision
      return draft
    },
    getSnapshot: () => stateRef.current,
    getPayload: (s) => ({
      service: s.service, answers: guestAnswers(s.posting), stepIndex: s.stepIndex, posting: postingPayload(s, catalog),
      openHouse: openHousePayload(s.oh), removal: s.oh.removeFile, accept: s.acceptToken, saveFirst: s.saveFirst,
      uninvite: s.uninvite,
    }),
    preparePayload: (payload) => withoutUploaded(payload, uploadedRef.current, removedRef.current),
    onSaving: (_s, payload) => {
      if (payload.openHouse.outline_file) setUpload({ status: "uploading", file: payload.openHouse.outline_file })
    },
    onSaved: (snapshot, payload) => {
      const { outline_file: file, remove_outline_file: removed } = payload.openHouse
      if (file) {
        uploadedRef.current.add(file)
        setUpload((prev) => (prev.file === file ? { status: "done", file: null } : prev))
      }
      if (removed) removedRef.current.add(payload.removal)
      if (!file && !removed) return
      setState((prev) => {
        const oh = { ...prev.oh }
        if (file && oh.pendingFile === file) Object.assign(oh, { pendingFile: null, outlineFile: { filename: file.name } })
        if (removed && oh.removeFile === snapshot.oh.removeFile) oh.removeFile = null
        return { ...prev, oh }
      })
    },
    onFailed: (_s, payload, err) => {
      if (err?.reason === "stale") setNotice("stale")
      if (err?.reason === "draft_expired") setPhase("expired")
      const file = payload.openHouse.outline_file
      if (!file) return
      // A file the server refuses is dropped; any other failure keeps it for a retry.
      const refused = err?.status === 422 && String(err.reason).startsWith("outline_file")
      if (refused) setState((prev) => (prev.oh.pendingFile === file ? { ...prev, oh: { ...prev.oh, pendingFile: null } } : prev))
      const status = !refused ? "failed" : err.reason === "outline_file_too_large" ? "tooLarge" : "unsupported"
      setUpload((prev) => (prev.file === file ? { status, file } : prev))
    },
  })

  // ── the guest draft ──────────────────────────────────────────────────────────

  // The quote scope a state's answers price, or null until they can be priced.
  const scopeFor = (s) => {
    const c = postingCtx(catalog, s.service, s.posting.bundle_tier)
    const answers = { ...s.posting, short_description: s.oh.short_description }
    return quoteScope(s.service, serviceOf(s.service).steps(s.posting, c), answers, c, s.oh)
  }

  // A resumed draft brings its own current price (or refusal); any other quote
  // held for the same answers is dropped, and without one the page re-quotes.
  const adoptQuote = (sig, result) => {
    quoteGen.current += 1
    setQuote(result && { sig, ...result })
    if (!result) setQuoteEpoch((n) => n + 1)
  }

  // `invited`: the draft an invite just opened, which has nothing to restore.
  const restore = ({ draft, price }, target = "wizard", invited = false) => {
    if (draft.reserved && !onAccountPath()) return openPostedProject()
    if (draft.reserved) {
      rememberWaiting(true)
      setWaiting(draft.waiting)
      return setPhase("waiting")
    }
    rememberWaiting(false)
    const next = guestState(draft)
    const sig = JSON.stringify(scopeFor(next))
    if (price?.quote) adoptQuote(sig, { status: "ready", quote: price.quote })
    else if (price?.reason) adoptQuote(sig, { status: price.reason === "combination_unavailable" ? "combination" : "unavailable" })
    else adoptQuote(sig, null)
    revisionRef.current = draft.revision
    stateRef.current = next
    setBookmark({ email: draft.email, reminderConsent: draft.reminderConsent })
    setInvitation(draft.invitation ?? null)
    setState(next)
    noteDraftEdit()
    setDirty(false)
    setNotice(invited ? null : "restored")
    setAccepted(null)
    // The changed-price notice belongs to the answers it was for (their signature).
    setChange(price?.change && price.quote ? { previous: price.change.previous, sig } : null)
    if (target === "wizard") leaveAccount()
    setPhase(target)
  }

  // Through the save queue, so a stale draft is refused and shown as any save's.
  const uninvite = async (slug) => {
    const draft = await saveCurrentDraft({ snapshot: { ...stateRef.current, uninvite: slug, saveFirst: dirty } })
    setInvitation(draft.invitation ?? null)
  }

  // An expired draft is left behind: the next answer starts a new one.
  const startOver = () => {
    rememberWaiting(false)
    setTerms(false)
    leaveAccount()
    revisionRef.current = null
    setState(initialState({}, null, "book_writing"))
    setInvitation(null)
    setDirty(false)
    setNotice(null)
    setChange(null)
    setPhase("entry")
  }

  const loadLatest = () =>
    resumeGuestDraft().then(restore).catch((err) => err.status === 410 && setPhase("expired"))

  useEffect(() => {
    const link = openReturnLink()
    const account = !link && onAccountPath()
    const reminder = account && fromReminder()
    const invited = !link && !account && arrivedInviting()
    ;(link || resumeGuestDraft()).then((result) => restore(result, account ? "account" : "wizard", invited)).catch((err) => {
      // No draft here, or an expired one.
      // The device that posted knows its post was deleted, even from the reminder.
      if (account && err.status === 404 && wasWaiting()) return setPhase("postDeleted")
      if (reminder && err.status === 404) return setPhase("reminderNoDraft")
      if (account && [404, 410].includes(err.status)) return setPhase("expired")
      if (account) leaveAccount()
      if (link && err.reason === "converted") return openPostedProject()
      if (!link || err.reason === "draft_expired") return setPhase(err.status === 410 ? "expired" : "entry")
      setLinkLines(linkPage(err.reason))
      setPhase("link")
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bookmark's send: the draft saves first (creating it if no answer has yet),
  // then the link is emailed. A draft found expired ends the dialog.
  const sendSaveLink = async (request) => {
    const ended = (err) => {
      if (err?.reason === "draft_expired") {
        setBookmarkOpen(false)
        setPhase("expired")
      }
      return err
    }
    await saveCurrentDraft({ snapshot: { ...stateRef.current, draftId: GUEST_DRAFT } }).catch((err) => {
      throw Object.assign(ended(err) || new Error("save failed"), { stage: "save" })
    })
    setState((prev) => ({ ...prev, draftId: GUEST_DRAFT }))
    const { draft } = await emailSaveLink(request).catch((err) => Promise.reject(ended(err)))
    setBookmark({ email: draft.email, reminderConsent: draft.reminderConsent })
    track("open_house_save_link", { service: stateRef.current.service })
  }

  // Autosave (debounced) once the first answer has started the draft.
  const halted = notice === "stale" || phase !== "wizard"
  useEffect(() => {
    if (!state.draftId || !dirty || halted || submitting) return
    const handle = setTimeout(() => saveCurrentDraft().catch(() => {}), 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.draftId, state.posting, state.oh, state.stepIndex, dirty, halted, submitting])

  // ── the quote ────────────────────────────────────────────────────────────────

  // Debounced, one in flight; a response for answers since changed is dropped.
  const answersScope = scopeFor(state)
  const answersSig = JSON.stringify(answersScope)
  const scope = phase === "wizard" ? answersScope : null
  const scopeSig = scope && answersSig
  const latest = useRef({})
  latest.current = { scope, scopeSig, answersSig }

  // A changed price, and a price just accepted, stop applying once the answers
  // that set the price change, even if they change back.
  useEffect(() => {
    if (change && change.sig !== answersSig) setChange(null)
    if (accepted && accepted !== answersSig) setAccepted(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answersSig])

  const requestQuote = async () => {
    const run = quoteRun.current
    if (run.busy) {
      run.again = true
      return
    }
    const { scope: asked, scopeSig: sig } = latest.current
    if (!asked) return
    const gen = quoteGen.current
    run.busy = true
    let result
    try {
      result = { status: "ready", quote: await fetchQuote(asked) }
    } catch (err) {
      result = { status: err.reason === "combination_unavailable" ? "combination" : "unavailable" }
    }
    run.busy = false
    if (gen === quoteGen.current && sig === latest.current.scopeSig) setQuote({ sig, ...result })
    if (run.again) {
      run.again = false
      requestQuote()
    }
  }

  useEffect(() => {
    if (!scopeSig || quoteRef.current?.sig === scopeSig) return
    const handle = setTimeout(requestQuote, 400)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeSig, quoteEpoch])

  const price = !scope ? { status: "idle" } : quote?.sig === scopeSig ? quote : { status: "loading" }
  const display = price.status === "ready" ? quoteDisplay(price.quote, ctx) : null
  // A changed price applies only to the answers it was for, and always shows
  // (and accepts) the current quote for them.
  const changeDue = !!change && change.sig === answersSig && price.status === "ready"
  const justAccepted = accepted === answersSig
  const priceBlocked = price.status !== "ready" || changeDue
  const hasNotice = ["unavailable", "combination"].includes(price.status) || changeDue || justAccepted

  // Analytics: every screen the guest reaches (the arrival, each step by its
  // id, the account and waiting screens), and the first price per service.
  // The service only where the page holds the draft's answers: a reserved
  // draft reopened on its waiting screen does not restore them.
  const screen = phase === "wizard" ? step.id : phase === "entry" ? (startPage ? "start" : "arrival") : phase
  useEffect(() => {
    if (phase === "loading") return
    const answered = phase === "wizard" || phase === "account"
    track("open_house_step", { service: answered ? state.service : null, step: screen, step_number: phase === "wizard" ? index + 1 : null })
  }, [phase, screen, index, state.service])

  const pricedRef = useRef(new Set())
  useEffect(() => {
    if (price.status !== "ready" || pricedRef.current.has(state.service)) return
    pricedRef.current.add(state.service)
    track("open_house_price_shown", { service: state.service, ...priceParams(price.quote) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price.status, state.service])

  // Through the save queue, so its refusals land in onFailed like any save's.
  // A newer price becomes the page's quote everywhere and is the one to accept.
  const acceptChange = async () => {
    setAccepting(true)
    // The answers this acceptance prices; a newer price for them is dropped once
    // they have changed, leaving the current answers' own quote untouched.
    const startedSig = latest.current.answersSig
    const startedEdits = editsRef.current
    try {
      const snapshot = { ...stateRef.current, acceptToken: price.quote.token, saveFirst: dirty }
      const result = await saveCurrentDraft({ snapshot })
      if (startedSig !== latest.current.answersSig || startedEdits !== editsRef.current) return
      if (result?.priceChanged) {
        adoptQuote(startedSig, { status: "ready", quote: result.priceChanged })
      } else {
        setChange(null)
        setAccepted(startedSig)
      }
    } catch {
      // The save queue's onFailed has shown what went wrong.
    } finally {
      setAccepting(false)
    }
  }

  // What the steps show on this page: amounts only from the server quote.
  const viewCtx = {
    ...ctx,
    guest: true,
    quotedOutline: state.posting.add_outline ? quotedOutline(price.quote) : undefined,
    openLevelsLine: display?.open
      ? (display.range ?? `${display.from ? "From " : ""}${display.value}`)
      : catalogLevelsRange(state.service, state.posting, ctx),
  }

  // ── answers ──────────────────────────────────────────────────────────────────

  const edit = (updater) => {
    editsRef.current += 1
    noteDraftEdit()
    setDirty(true)
    setNotice((prev) => (prev === "restored" ? null : prev))
    setState((prev) => ({ ...updater(prev), draftId: GUEST_DRAFT }))
  }

  const setField = (key, value) => {
    if (key === "outline_choice" && value === "upgrade") return setField("bundle_tier", PLATINUM)
    if (key === "bundle_tier" && BUNDLE_COMPOSITION[value]?.outline) setUpload({ status: "idle", file: null })
    edit((prev) => answerUpdate(prev, key, value))
  }

  // Upload or paste saves at once; only the chosen source is kept (as signed in).
  const chooseOutlineSource = (source) => {
    const prevOh = stateRef.current.oh
    if (!source || source === prevOh.outline_source) return
    const changes = source === "upload"
      ? { outline_source: source, outline_text: "" }
      : { outline_source: source, outlineFile: null, pendingFile: null }
    if (source === "paste") setUpload({ status: "idle", file: null })
    stateRef.current = { ...stateRef.current, draftId: GUEST_DRAFT, oh: { ...prevOh, ...changes } }
    edit((prev) => ({ ...prev, oh: { ...prev.oh, ...changes } }))
    saveCurrentDraft().catch(() => {})
  }

  const chooseOutlineFile = (file) => {
    if (!isOutlineFile(file)) return setUpload({ status: "unsupported", file })
    if (file.size > OUTLINE_MAX_BYTES) return setUpload({ status: "tooLarge", file })
    const removal = stateRef.current.oh.outlineFile ? ++removalRef.current : stateRef.current.oh.removeFile
    setUpload({ status: "uploading", file })
    edit((prev) => ({ ...prev, oh: { ...prev.oh, pendingFile: file, outlineFile: null, removeFile: removal } }))
  }

  const removeOutline = () => {
    const removal = ++removalRef.current
    setUpload({ status: "idle", file: null })
    edit((prev) => ({ ...prev, oh: { ...prev.oh, outlineFile: null, pendingFile: null, removeFile: removal } }))
  }

  // ── navigation and posting ───────────────────────────────────────────────────

  const start = (key) => {
    if (key !== stateRef.current.service || !stateRef.current.draftId) {
      // Review's terms tick belongs to the answers it was given for.
      setTerms(false)
      const fresh = initialState({}, null, key)
      if (stateRef.current.draftId) edit(() => fresh)
      else setState(fresh)
    }
    setPhase("wizard")
  }

  const goTo = (target, focusKey = null) => {
    setShowErr(!!focusKey)
    setState((prev) => ({ ...prev, stepIndex: target }))
    if (stateRef.current.draftId) setDirty(true)
    window.requestAnimationFrame(() => (focusKey ? focusField(focusKey) : topRef.current?.scrollIntoView({ block: "start" })))
  }

  const handleContinue = () => {
    const issues = stepIssues(step, values, ctx, state.oh)
    if (!issues.length) return goTo(index + 1)
    setShowErr(true)
    window.requestAnimationFrame(() => focusField(issues[0][0]))
  }

  const handlePost = async () => {
    const blocked = steps.findIndex((s) => stepIssues(s, values, ctx, state.oh).length)
    if (blocked >= 0 && blocked !== index) return goTo(blocked, stepIssues(steps[blocked], values, ctx, state.oh)[0][0])
    setShowErr(true)
    if (!terms) return window.requestAnimationFrame(() => focusField("terms"))
    if (priceBlocked || submitting) return window.requestAnimationFrame(() => focusField("price"))

    // The latest answers save, then the price shown is accepted at the revision
    // that save returned (one queued write, as acceptChange's), always: accepting
    // the current price again is harmless. A newer price stays here to be accepted.
    setSubmitting(true)
    const sig = latest.current.answersSig
    const shown = price.quote
    try {
      const snapshot = { ...stateRef.current, acceptToken: shown.token, saveFirst: true }
      const result = await saveCurrentDraft({ requireLatest: true, snapshot })
      if (result?.priceChanged) {
        adoptQuote(sig, { status: "ready", quote: result.priceChanged })
        setChange({ previous: quoteAmounts(shown), sig })
        setSubmitting(false)
        return window.requestAnimationFrame(() => focusField("price"))
      }
      continueToAccount()
    } catch {
      setSubmitting(false)
    }
  }

  // ── the account screen ──────────────────────────────────────────────────────────

  // No answer can be pending here: Review's post saved through the queue
  // (requireLatest) before it came to this screen, and a load resumes the saved
  // draft. So the revision held is the one saved, and the POST carries it.
  const postAccount = async (fields) => {
    let result
    try {
      result = await reserveAccount(revisionRef.current, fields)
    } catch (err) {
      const routed = routeRefusal(err)
      if (!routed) throw err
      return routed
    }
    rememberWaiting(true)
    setWaiting(result.waiting)
    setPhase("waiting")
    track("open_house_account_created", { service: stateRef.current.service, ...priceParams(quoteRef.current?.quote) })
  }

  // Resumed and routed as the page does; a resume that fails stays the screen's to show.
  const reload = () =>
    resumeGuestDraft().then((result) => restore(result)).catch((err) => {
      if (err.status !== 410) throw err
      setPhase("expired")
    })

  // The refusals about the draft itself leave the screen: true, or the resume doing so.
  const routeRefusal = (err) => {
    const { reason, data = {} } = err || {}
    if (reason === "stale" || reason === "converted") return reload()
    if (reason === "price_changed" && data.quote) return backToReview(data.quote) ?? true
    if (reason === "draft_expired") setPhase("expired")
    else if (reason === "draft_incomplete") backToIssue(data.issues || [])
    else return false
    return true
  }

  const backToSteps = () => {
    leaveAccount()
    setNotice(null)
    setPhase("wizard")
  }

  // The new price becomes the page's quote, with the changed-price notice to
  // accept: from the price the guest last saw, or the one a resume reported.
  const backToReview = (current) => {
    const sig = latest.current.answersSig
    const held = quoteRef.current?.sig === sig && quoteRef.current.status === "ready" ? quoteRef.current.quote : null
    const previous = change?.sig === sig ? change.previous : held && quoteAmounts(held)
    if (!previous) return reload()
    adoptQuote(sig, { status: "ready", quote: current })
    setChange({ previous, sig })
    backToSteps()
    goTo(steps.length - 1)
  }

  // The first step with an issue, as Review's post finds it: the server's
  // issues where they name a step's field, else the page's own check.
  const backToIssue = (issues) => {
    const server = issues.map((issue) => issueTarget(issue, steps, values, ctx, service.aliases)).filter(Boolean)
    const target = server.sort((a, b) => a.index - b.index)[0]
    const blocked = steps.findIndex((s) => stepIssues(s, values, ctx, state.oh).length)
    backToSteps()
    if (target) goTo(target.index, target.key)
    else if (blocked >= 0) goTo(blocked, stepIssues(steps[blocked], values, ctx, state.oh)[0][0])
    else goTo(0, null)
  }

  // ── render ─────────────────────────────────────────────────────────────────────

  const priceNotice = (
    <PriceNotice
      status={price.status}
      change={changeDue ? changeLines({ previous: change.previous, current: quoteAmounts(price.quote), currency: price.quote.currency }) : null}
      accepted={justAccepted}
      accepting={accepting}
      onRetry={() => {
        setQuote(null)
        requestQuote()
      }}
      onReview={() => goTo(0)}
      onAccept={acceptChange}
    />
  )

  let body
  if (phase === "loading") {
    body = <div aria-busy="true" className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
  } else if (phase === "expired") {
    body = <MessagePage lines={[GUEST.draftExpiredHeading, GUEST.draftExpiredBody(draftInactivityDays)]} onStart={startOver} />
  } else if (phase === "postDeleted") {
    body = <WaitingColumn><PostExpired onStartNew={startOver} /></WaitingColumn>
  } else if (phase === "reminderNoDraft") {
    body = <MessagePage lines={[CONFIRMATION.reminderNoDraft.heading, CONFIRMATION.reminderNoDraft.body]} onStart={startOver} />
  } else if (phase === "account") {
    body = <AccountScreen account={account} askReminder={!bookmark.reminderConsent} signInPath={links.signIn} onPost={postAccount} />
  } else if (phase === "waiting") {
    body = <WaitingScreen waiting={waiting} onStartNew={startOver} />
  } else if (phase === "link") {
    body = <MessagePage lines={linkLines} onStart={startOver} />
  } else if (phase === "entry") {
    const Entry = startPage ? StartPage : Arrival
    body = <Entry catalog={catalog} links={links} onStart={start} openWriting={arrivesWriting()} />
  } else {
    body = (
      <div ref={topRef} className="mx-auto flex max-w-[1040px] flex-col gap-4 px-8 pt-8 pb-14 max-[561px]:px-4">
        <div className="flex min-h-5 justify-end text-[12px] font-semibold text-muted-foreground">
          {savedAt && !saveError && (
            <span className="flex items-center gap-1.5"><Check className="size-3.5 text-done" strokeWidth={2.4} />{GUEST.saved}</span>
          )}
          {dirty && !savedAt && !saveError && notice !== "stale" && <span>{GUEST.saving}</span>}
        </div>
        {notice === "restored" && <Alert tone="info">{GUEST.restored}</Alert>}
        {notice === "stale" && <Alert action={GUEST.loadLatest} onAction={loadLatest}>{GUEST.newerVersion}</Alert>}
        {notice !== "stale" && saveError && (
          <Alert action={GUEST.saveRetry} onAction={retrySave} busy={retryingSave || submitting}>{GUEST.saveFailed}</Alert>
        )}
        {hasNotice && <div className={RAIL_BAR_BAND}>{priceNotice}</div>}
        <InvitationStrip
          invitation={invitation} tier={state.posting.tier} onTier={(tier) => setField("tier", tier)} onRemove={uninvite}
        />

        <WizardShell
          title={step.title}
          sub={step.sub}
          steps={steps.map((s) => ({ id: s.id, title: s.node }))}
          activeIndex={index}
          completedThrough={index - 1}
          stepperCollapseAt={STEPPER_COLLAPSE.service}
          onJump={(i) => goTo(i)}
          onBack={() => (index === 0 ? setPhase("entry") : goTo(index - 1))}
          onContinue={isReview ? handlePost : handleContinue}
          continueLabel={isReview ? "Post project" : "Continue"}
          continueDisabled={submitting}
          priceRail={
            <GuestPriceRail price={price} display={display} notice={hasNotice && priceNotice} onSave={() => setBookmarkOpen(true)} />
          }
          footerNote={<SaveLaterButton onClick={() => setBookmarkOpen(true)} />}
        >
          {isReview ? (
            <ReviewStep
              sections={reviewSections(steps, values, viewCtx, state.oh)}
              onEdit={(i) => goTo(i)}
              terms={terms}
              onTerms={setTerms}
              termsError={showErr && !terms}
              posting={submitting}
              price={
                <ReviewPrice loading={price.status === "loading"} display={display} notice={hasNotice && priceNotice} required={showErr && changeDue} />
              }
            />
          ) : (
            <StepFields
              step={step}
              values={values}
              ctx={viewCtx}
              oh={state.oh}
              showErr={showErr}
              setField={setField}
              fieldNodes={fieldNodes}
              outline={{
                upload, onSource: chooseOutlineSource, onFile: chooseOutlineFile, onRetry: retrySave, onRemove: removeOutline,
              }}
            />
          )}
        </WizardShell>
        <Bookmark open={bookmarkOpen} onOpenChange={setBookmarkOpen} days={draftInactivityDays} saved={bookmark} onSend={sendSaveLink} />
      </div>
    )
  }

  const [lead, signIn] = GUEST.signIn.split(/(?<=\?) /)
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-card px-8 py-3.5 max-[561px]:px-4">
        <BrandLockup className="h-[26px]" />
        <p className="text-[12.5px] text-muted-foreground">
          {lead} <a href={links.signIn} className="font-semibold text-foreground underline-offset-2 hover:underline">{signIn}</a>
        </p>
      </header>
      <main>{body}</main>
    </div>
  )
}
